<template>
  <v-alert
    v-if="organizationInvitation"
    class="flex-none"
    type="info"
    variant="tonal"
    data-testid="account-organization-invitation"
  >
    <div class="font-bold">
      {{
        t("account.organizationInvitation.title", {
          supervisorOrgName: organizationInvitation.supervisorOrgName,
        })
      }}
    </div>
    <div class="text-sm">{{ t("account.organizationInvitation.message") }}</div>
    <div class="flex gap-2 mt-3">
      <v-btn
        color="primary"
        variant="flat"
        size="small"
        append-icon="mdi-open-in-new"
        :disabled="refusing"
        data-testid="account-organization-invitation-sign-button"
        @click="
          openInNewTab('contractSigning', {
            accountId,
            organizationInvitationId: organizationInvitation.id,
          })
        "
        >{{ t("account.organizationInvitation.sign") }}</v-btn
      >
      <v-btn
        variant="outlined"
        size="small"
        :disabled="refusing"
        data-testid="account-organization-invitation-refuse-button"
        @click="confirmingRefusal = true"
        >{{ t("account.organizationInvitation.refuse") }}</v-btn
      >
    </div>
    <v-dialog v-model="confirmingRefusal" max-width="500" :persistent="refusing">
      <v-card>
        <v-card-title class="flex items-center">
          <span>{{ t("account.organizationInvitation.refuse") }}</span>
          <v-spacer />
          <v-btn
            icon="mdi-close"
            :aria-label="t('common.close')"
            variant="text"
            size="small"
            :disabled="refusing"
            @click="confirmingRefusal = false"
          />
        </v-card-title>
        <v-card-text>{{
          t("account.organizationInvitation.refuseConfirm", {
            supervisorOrgName: organizationInvitation.supervisorOrgName,
          })
        }}</v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn :disabled="refusing" @click="confirmingRefusal = false">{{
            t("common.close")
          }}</v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="error"
            :loading="refusing"
            :disabled="refusing"
            data-testid="account-organization-invitation-refuse-confirm"
            @click="refuse(organizationInvitation.id)"
            >{{ t("account.organizationInvitation.refuse") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-alert>
</template>

<script lang="ts" setup>
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import {
  accountOrganizationInvitationsFullPath,
  type AccountOrganizationInvitation,
} from "@aircap/common/accountOrganizationInvitation";
import { captureException } from "@sentry/vue";
import { collection, query, where } from "firebase/firestore";
import { computed, ref } from "vue";

const props = defineProps<{
  accountId: string;
}>();

const { t } = useI18n();
const { showErrorToast } = useToast();
const openInNewTab = useOpenInNewTab();

const { data: organizationInvitations } = useCollection<AccountOrganizationInvitation>(
  computed(() =>
    query(
      collection<AccountOrganizationInvitation>(db, accountOrganizationInvitationsFullPath),
      where<AccountOrganizationInvitation>("accountId", "==", props.accountId),
      where<AccountOrganizationInvitation>("status", "==", "pending"),
    ),
  ),
);
const organizationInvitation = computed(() => organizationInvitations.value[0]);

const confirmingRefusal = ref(false);
const refusing = ref(false);

const refuse = async (organizationInvitationId: string) => {
  refusing.value = true;
  try {
    await call("refuseAccountOrganizationInvitation")({
      accountId: props.accountId,
      organizationInvitationId,
    });
    confirmingRefusal.value = false;
  } catch (cause) {
    const error = new Error(`Error refusing organization invitation ${organizationInvitationId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    refusing.value = false;
  }
};
</script>
